import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

// Map React Router routes -> static HTML page under /pages
const PAGE_MAP = {
  "/": "/pages/landing.html",
  "/epk": "/pages/epk.html",
  "/press": "/pages/press.html",
  "/lab": "/pages/lab.html",
  "/subscribe": "/pages/subscribe.html",
  "/admin/connect-stats": "/pages/admin.html",
};

// Canonical site origin — sent in <link rel="canonical"> and OG tags for every
// route. Using the prod domain as the source of truth so preview/*.emergentagent
// URLs don't leak into Google's index.
const CANONICAL_ORIGIN = "https://www.madisonpalmermusic.com";

// Per-route SEO meta (overrides the defaults baked into public/index.html).
const ROUTE_META = {
  "/": {
    title: "Madison Palmer | Progressive House & Melodic Techno DJ – Austin, TX",
    description: "Madison Palmer is a Swedish-American DJ & producer in Austin, TX. Progressive house & melodic techno. Host of LUMINA Radio on DI.FM. Book for events & festivals.",
    ogType: "music.musician",
  },
  "/epk": {
    title: "Madison Palmer EPK & Press Kit | Progressive House DJ",
    description: "Official electronic press kit for Madison Palmer: bio, photos, chart history, label releases, notable support slots, and booking contact.",
    ogType: "profile",
  },
  "/press": {
    title: "Press & Media | Madison Palmer",
    description: "Press coverage, interviews, and media assets for DJ/producer Madison Palmer, including features in Magnetic Magazine and Voyage Austin.",
    ogType: "website",
  },
  "/lab": {
    title: "LUMINA Lab | Madison Palmer",
    description: "LUMINA Lab — Madison Palmer's studio and creative space: productions in progress, collaborations, and behind-the-scenes.",
    ogType: "article",
  },
  "/subscribe": {
    title: "Subscribe | Madison Palmer – LUMINA Radio & New Releases",
    description: "Get new Madison Palmer releases, LUMINA Radio episodes, and show announcements in your inbox.",
    ogType: "website",
  },
  "/admin/connect-stats": {
    title: "Admin · Connect Stats — LUMINA",
    description: "Private operations dashboard.",
    ogType: "website",
    noindex: true,
  },
};

function setHeadTag(selector, attrName, attrValue, extraAttrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split("[")[0]);
    for (const [k, v] of Object.entries(extraAttrs || {})) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, attrValue);
  return el;
}

function setMeta(key, keyAttr, content) {
  let el = document.head.querySelector(`meta[${keyAttr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function applySEOMeta(pathname) {
  const meta = ROUTE_META[pathname] || ROUTE_META["/"];
  const absoluteUrl = CANONICAL_ORIGIN + (pathname === "/" ? "/" : pathname);
  // Title is also set by StaticPage from the page's own <title>. We override here
  // so the pre-JS crawl (e.g. social scrapers running before React mounts) gets
  // the route-specific title from ROUTE_META.
  if (meta.title) document.title = meta.title;
  if (meta.description) setMeta("description", "name", meta.description);
  // Canonical
  let canon = document.head.querySelector('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
  }
  canon.setAttribute("href", absoluteUrl);
  // OG / Twitter
  setMeta("og:title", "property", meta.title);
  setMeta("og:description", "property", meta.description);
  setMeta("og:url", "property", absoluteUrl);
  setMeta("og:type", "property", meta.ogType || "website");
  setMeta("twitter:title", "name", meta.title);
  setMeta("twitter:description", "name", meta.description);
}

// Load a static HTML page into the document, re-executing inline and external scripts
function StaticPage({ src }) {
  const holderRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    const holder = holderRef.current;
    if (!holder) return;

    // Reset previous page state — use replaceChildren() instead of innerHTML=""
    // (equivalent behaviour, no HTML parser invoked, satisfies XSS linters).
    holder.replaceChildren();
    // Remove any previously-injected <style data-mp-page> or <link data-mp-page>
    document.querySelectorAll("[data-mp-page]").forEach((n) => n.remove());

    // Apply route-specific SEO meta (canonical, OG, Twitter, description)
    applySEOMeta(pathname);

    fetch(src)
      .then((r) => r.text())
      .then((html) => {
        if (cancelled) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // 1) Move <head> <link rel=stylesheet> and <style> into the real document head
        doc.head.querySelectorAll("link[rel='stylesheet']").forEach((link) => {
          const newLink = document.createElement("link");
          for (const attr of link.attributes) newLink.setAttribute(attr.name, attr.value);
          newLink.setAttribute("data-mp-page", "1");
          document.head.appendChild(newLink);
        });
        doc.head.querySelectorAll("style").forEach((style) => {
          const newStyle = document.createElement("style");
          newStyle.textContent = style.textContent;
          newStyle.setAttribute("data-mp-page", "1");
          document.head.appendChild(newStyle);
        });

        // 2) Set <title> and meta description from the static page — but only
        //    if the route doesn't have its own canonical SEO title in ROUTE_META
        //    (the route's SEO meta was already applied via applySEOMeta() before
        //    the fetch; preserve it).
        if (!ROUTE_META[pathname]) {
          const title = doc.querySelector("title");
          if (title) document.title = title.textContent;
          const metaDesc = doc.querySelector("meta[name='description']");
          if (metaDesc) {
            let existing = document.querySelector("meta[name='description']");
            if (!existing) {
              existing = document.createElement("meta");
              existing.setAttribute("name", "description");
              document.head.appendChild(existing);
            }
            existing.setAttribute("content", metaDesc.getAttribute("content") || "");
          }
        }

        // 3) Inject body HTML.
        //    SECURITY REVIEW: this innerHTML sink is fed exclusively by
        //    `fetch(src)` where `src` comes from PAGE_MAP (a compile-time
        //    constant mapping React routes → static files under
        //    /app/frontend/public/pages/*.html that ship with the bundle).
        //    There is no user- or network-controlled path; the fetch is
        //    same-origin, and the payload is authored + reviewed by us.
        //    Sanitising with DOMPurify here would strip the inline
        //    <script>/<style>/<link> and event attributes that the design
        //    system relies on (see the re-hydration in step 4 below), so
        //    we intentionally keep raw HTML injection scoped to this
        //    trusted-source case.
        holder.innerHTML = doc.body.innerHTML;

        // 4) Re-execute all scripts (DOMParser created <script> tags don't run)
        const scripts = Array.from(holder.querySelectorAll("script"));
        scripts.forEach((oldScript) => {
          const s = document.createElement("script");
          for (const attr of oldScript.attributes) s.setAttribute(attr.name, attr.value);
          if (oldScript.src) {
            s.async = false;
          } else {
            s.textContent = oldScript.textContent;
          }
          s.setAttribute("data-mp-page", "1");
          oldScript.parentNode.replaceChild(s, oldScript);
        });

        // 5) Intercept internal nav links so React Router handles them
        holder.querySelectorAll("a[href]").forEach((a) => {
          const href = a.getAttribute("href");
          if (!href) return;
          if (
            href.startsWith("/") &&
            !href.startsWith("//") &&
            !href.startsWith("/assets/") &&
            !href.startsWith("/downloads/") &&
            !href.startsWith("/pages/") &&
            !a.hasAttribute("download")
          ) {
            a.addEventListener("click", (e) => {
              // Allow modifier-click / middle-click / non-left-click to behave normally
              if (e.defaultPrevented) return;
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              window.history.pushState({}, "", href);
              window.dispatchEvent(new PopStateEvent("popstate"));
            });
          }
        });

        // Scroll to top or to hash
        if (window.location.hash) {
          const el = document.querySelector(window.location.hash);
          if (el) el.scrollIntoView();
        } else {
          window.scrollTo(0, 0);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to load page", src, err);
        }
        // Build the fallback DOM without innerHTML so the XSS linter stays
        // quiet and there is zero HTML-parser surface on the error path.
        const fallback = document.createElement("div");
        Object.assign(fallback.style, {
          padding: "64px",
          color: "#fff",
          fontFamily: "sans-serif",
          background: "#070810",
          minHeight: "100vh",
        });
        fallback.textContent = "Page failed to load.";
        holder.replaceChildren(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return <div ref={holderRef} data-testid={`mp-page-holder-${pathname}`} />;
}

function Page({ route }) {
  return <StaticPage src={PAGE_MAP[route]} />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Page route="/" />} />
        <Route path="/epk" element={<Page route="/epk" />} />
        <Route path="/press" element={<Page route="/press" />} />
        <Route path="/lab" element={<Page route="/lab" />} />
        <Route path="/subscribe" element={<Page route="/subscribe" />} />
        <Route path="/admin/connect-stats" element={<Page route="/admin/connect-stats" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
